import { RouteObject } from 'react-router-dom';

import { canAccessUserCreation } from '@/utils/adminRoles';
import {
    protectedRoutes,
    protectedRoutesForCSLTIUser,
    protectedRoutesForCSUser,
    protectedRoutesForPublishUser,
    protectedRoutesForSubscriptionManager,
    protectedRoutesForSuperAdmin,
    protectedRoutesForUserCreation,
} from './protected';

const getRouteChildren = (routes: RouteObject[]): RouteObject[] => routes[0]?.children ?? [];

const mergeRouteChildren = (routeSets: RouteObject[][]): RouteObject[] => {
    const byPath = new Map<string, RouteObject>();

    routeSets.forEach((routes) => {
        getRouteChildren(routes).forEach((route) => {
            const path = route.path ?? '';
            if (!byPath.has(path)) {
                byPath.set(path, route);
            }
        });
    });

    const ordered: RouteObject[] = [];
    const seen = new Set<string>();

    const appendRoutes = (routes: RouteObject[], includeCatchAll: boolean) => {
        getRouteChildren(routes).forEach((route) => {
            const path = route.path ?? '';
            if (path === '*' && !includeCatchAll) return;
            if (seen.has(path) || !byPath.has(path)) return;
            seen.add(path);
            ordered.push(byPath.get(path)!);
        });
    };

    routeSets.forEach((routes) => appendRoutes(routes, false));

    byPath.forEach((route, path) => {
        if (path !== '*' && !seen.has(path)) {
            seen.add(path);
            ordered.push(route);
        }
    });

    const catchAll = byPath.get('*');
    if (catchAll) {
        ordered.push(catchAll);
    }

    return ordered;
};

export const getProtectedRoutesForRoles = (roles: string[] = []): RouteObject[] => {
    const roleSet = new Set(roles);
    const routeSets: RouteObject[][] = [];

    if (roleSet.has('ROLE_SUPER_ADMIN')) {
        routeSets.push(protectedRoutesForSuperAdmin);
    }
    if (roleSet.has('ROLE_ADMIN') || roleSet.has('ROLE_CORPORATE_ADMIN')) {
        routeSets.push(protectedRoutes);
    }
    if (roleSet.has('ROLE_SUBSCRIPTION_MANAGER')) {
        routeSets.push(protectedRoutesForSubscriptionManager);
    }
    if (roleSet.has('ROLE_CS_USER')) {
        routeSets.push(protectedRoutesForCSUser);
    }
    if (roleSet.has('ROLE_CS_LTI_USER')) {
        routeSets.push(protectedRoutesForCSLTIUser);
    }
    if (roleSet.has('ROLE_PUBLISH')) {
        routeSets.push(protectedRoutesForPublishUser);
    }
    if (canAccessUserCreation(roles) && !roleSet.has('ROLE_SUPER_ADMIN')) {
        routeSets.push(protectedRoutesForUserCreation);
    }

    // Preserve previous fallback for unrecognized / partial role combinations.
    if (!routeSets.length) {
        return protectedRoutes;
    }

    const appRoute = (routeSets[0][0] ?? protectedRoutes[0]) as RouteObject;

    return [
        {
            ...appRoute,
            children: mergeRouteChildren(routeSets),
        },
    ];
};
